/**
 * SignerStateCard — per-signer status + per-state action surface for a
 * DocuSign envelope. Implements the 10 states from
 * docs/design/DOCUSIGN_UI_DESIGN.md §4.
 *
 * Backend integration: docusignAgent.fetchSigningUrl /
 * docusignAgent.resendSigningEmail. This component owns its own
 * loading state for the resend button and surfaces RateLimitError
 * via a disabled-by-cap state with describedby tooltip.
 */

import React, { useState } from 'react';
import {
  fetchSigningUrl,
  resendSigningEmail,
  RateLimitError,
} from '../../agents/docusignAgent';
import './SignerStateCard.css';

const RESEND_CAP = 5;

const STATUS_COPY = {
  pending: 'Pending',
  awaiting: 'Awaiting signer',
  signed: (date) => `Signed ${date}`,
  voided: (date, reason) =>
    reason ? `Voided ${date} — Reason: ${reason}` : `Voided ${date}`,
  declined: (date) => `Declined ${date}`,
  idvRequired: 'Identity verification required',
  idvHelper:
    'Spouse will enter the Access Code you provided before signing.',
  idvPhoneHelper:
    'Spouse will confirm a code sent by text or phone call before signing.',
};

const BADGE_COPY = {
  embedded: 'Sign in app',
  remote: 'Sent by email',
};

const PROVISIONING_RETRY_MAX_ATTEMPTS = 4;
const PROVISIONING_RETRY_BASE_DELAY_MS = 350;

const isProvisioningPendingError = (text) =>
  /envelope not yet provisioned/i.test(String(text || ''));

const delay = (ms) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

const fetchSigningUrlWithProvisioningRetry = async (documentId) => {
  let lastErr = null;
  for (let attempt = 1; attempt <= PROVISIONING_RETRY_MAX_ATTEMPTS; attempt += 1) {
    try {
      return await fetchSigningUrl(documentId);
    } catch (err) {
      const text = err?.message || '';
      lastErr = err;
      if (!isProvisioningPendingError(text) || attempt === PROVISIONING_RETRY_MAX_ATTEMPTS) {
        throw err;
      }
      await delay(PROVISIONING_RETRY_BASE_DELAY_MS * attempt);
    }
  }
  throw lastErr || new Error('Failed to mint signing URL');
};

const normalizeSignError = (text) => {
  if (!text) return "Couldn't open the signing experience. Please try again.";
  if (/envelope not yet provisioned/i.test(text)) {
    return 'Your signing session is still being prepared. Please try again in a moment.';
  }
  return text;
};

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch (_) {
    return value;
  }
};

const SignerStateCard = ({
  documentId,
  signature,
  isSelf = false,
  isSender = false,
  onResendNotify,
  onSignError,
  onSignSessionLaunched,
}) => {
  const [resendLoading, setResendLoading] = useState(false);
  const [signLoading, setSignLoading] = useState(false);
  const [localResendCount, setLocalResendCount] = useState(
    signature?.resend_count ?? 0,
  );
  const [capHit, setCapHit] = useState(localResendCount >= RESEND_CAP);
  const [signError, setSignError] = useState(null);

  if (!signature) {
    // Defensive empty state (design §4.3 row #10 "missing").
    return (
      <div className="signer-card signer-card--empty" role="status">
        <p>No signer data available.</p>
      </div>
    );
  }

  const status = signature.status || 'pending';
  const mode = signature.signing_mode || 'embedded';
  // Phone Authentication (current) supersedes the legacy sender-chosen
  // Access Code; older envelopes still carry access_code_required only.
  const phoneAuthRequired = !!signature.phone_auth_required;
  const accessCodeRequired = !!signature.access_code_required;
  const idvRequired = phoneAuthRequired || accessCodeRequired;

  const isVoided = status === 'voided';
  const isDeclined = status === 'declined';
  const isSigned = status === 'signed';
  const isPending = status === 'pending';

  const badgeKind = mode === 'remote' ? 'remote' : 'embedded';
  const badgeInactive = isVoided || isDeclined;

  const handleSignNow = async () => {
    setSignError(null);
    setSignLoading(true);
    try {
      const result = await fetchSigningUrlWithProvisioningRetry(documentId);
      if (result?.signing_url) {
        // New tab per design §4.3 row #1.
        window.open(result.signing_url, '_blank', 'noopener,noreferrer');
        onSignSessionLaunched?.({ documentId, signatureId: signature?.id });
      } else if (result?.message) {
        setSignError(result.message);
      } else {
        setSignError("Couldn't open the signing experience. Please try again.");
      }
    } catch (err) {
      const text =
        err?.message || "Couldn't open the signing experience. Please try again.";
      const normalized = normalizeSignError(text);
      setSignError(normalized);
      onSignError?.(normalized);
    } finally {
      setSignLoading(false);
    }
  };

  const handleResend = async () => {
    if (capHit || resendLoading) return;
    setResendLoading(true);
    try {
      await resendSigningEmail(documentId, signature.id);
      const next = localResendCount + 1;
      setLocalResendCount(next);
      onResendNotify?.({
        kind: 'success',
        text: `Email re-sent to ${signature.signer_email || signature.signer_name || 'signer'}`,
      });
    } catch (err) {
      if (err instanceof RateLimitError) {
        setCapHit(true);
        setLocalResendCount(RESEND_CAP);
        onResendNotify?.({
          kind: 'error',
          text:
            'Resend limit reached for this signer (5/day). They should check spam/promotions, or you can void and re-send the document.',
        });
      } else {
        onResendNotify?.({
          kind: 'error',
          text: "We couldn't resend that email. Please try again.",
        });
      }
    } finally {
      setResendLoading(false);
    }
  };

  const renderBody = () => {
    if (isVoided) {
      return (
        <p className="signer-card__status">
          <span className="signer-card__status-icon" aria-hidden="true">⊘</span>
          {STATUS_COPY.voided(formatDate(signature.voided_at), signature.void_reason)}
        </p>
      );
    }
    if (isDeclined) {
      return (
        <p className="signer-card__status">
          <span className="signer-card__status-icon" aria-hidden="true">❌</span>
          {STATUS_COPY.declined(formatDate(signature.declined_at))}
        </p>
      );
    }
    if (isSigned) {
      return (
        <p className="signer-card__status signer-card__status--signed">
          <span className="signer-card__status-icon" aria-hidden="true">✅</span>
          {STATUS_COPY.signed(formatDate(signature.signed_at))}
        </p>
      );
    }
    // Pending
    if (mode === 'embedded') {
      return (
        <p className="signer-card__status">
          <span className="signer-card__status-icon" aria-hidden="true">⏳</span>
          {STATUS_COPY.pending}
        </p>
      );
    }
    if (mode === 'remote') {
      return (
        <>
          {idvRequired && (
            <div className="signer-card__idv">
              <span className="signer-card__status-icon" aria-hidden="true">🔒</span>
              <div>
                <p className="signer-card__idv-label">{STATUS_COPY.idvRequired}</p>
                <p className="signer-card__idv-helper">
                  {phoneAuthRequired ? STATUS_COPY.idvPhoneHelper : STATUS_COPY.idvHelper}
                </p>
              </div>
            </div>
          )}
          <p className="signer-card__status">
            <span className="signer-card__status-icon" aria-hidden="true">⏳</span>
            {STATUS_COPY.awaiting}
            {signature.last_sent_at && (
              <> · Last sent {formatDate(signature.last_sent_at)}</>
            )}
          </p>
        </>
      );
    }
    return null;
  };

  const renderActions = () => {
    if (isVoided || isDeclined || isSigned) {
      // Signed: a personal "Download my copy" link only for the signer.
      if (isSigned && isSelf) {
        return (
          <a
            className="signer-card__action signer-card__action--link"
            href={`/api/documents/${documentId}/download`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download my copy
          </a>
        );
      }
      return null;
    }

    // Embedded + pending → Sign Now (only the signer themself).
    if (mode === 'embedded' && isSelf && isPending) {
      return (
        <>
          <button
            type="button"
            className="signer-card__action signer-card__action--primary"
            onClick={handleSignNow}
            disabled={signLoading}
            aria-busy={signLoading || undefined}
          >
            {signLoading ? 'Opening…' : 'Sign Now →'}
          </button>
          {signError && (
            <p className="signer-card__error" role="alert">{signError}</p>
          )}
        </>
      );
    }

    // Remote + self + pending → "Check your email" CTA so the remote signer
    // sees an actionable hint rather than an empty action area.
    if (mode === 'remote' && isSelf && isPending) {
      const email = signature.signer_email;
      return (
        <div className="signer-card__idv" role="status">
          <span className="signer-card__status-icon" aria-hidden="true">📧</span>
          <div>
            <p className="signer-card__idv-label">Check your email</p>
            <p className="signer-card__idv-helper">
              {email
                ? <>We sent a signing link to <strong>{email}</strong>.</>
                : 'We sent a signing link to your email.'}
              {signature.last_sent_at && (
                <> Last sent {formatDate(signature.last_sent_at)}.</>
              )}
            </p>
          </div>
        </div>
      );
    }

    // Remote + pending → Resend email (only the sender).
    if (mode === 'remote' && isSender && isPending) {
      const tooltipId = `resend-tooltip-${signature.id}`;
      return (
        <div className="signer-card__resend-row">
          <button
            type="button"
            className="signer-card__action signer-card__action--secondary"
            onClick={handleResend}
            disabled={capHit || resendLoading}
            aria-busy={resendLoading || undefined}
            aria-describedby={capHit ? tooltipId : undefined}
          >
            {resendLoading ? 'Resending…' : 'Resend email'}
          </button>
          <span
            className={
              'signer-card-resend-count' +
              (capHit ? ' signer-card-resend-count--capped' : '')
            }
            aria-live="polite"
          >
            Resends today: {localResendCount} / {RESEND_CAP}
          </span>
          {capHit && (
            <span id={tooltipId} className="signer-card__tooltip" role="tooltip">
              Resend limit reached for today (5/5). Try again tomorrow, or void
              and re-send the document.
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div
      className={
        'signer-card' +
        (isVoided ? ' signer-card--voided' : '') +
        (isDeclined ? ' signer-card--declined' : '') +
        (isSigned ? ' signer-card--signed' : '')
      }
      aria-label={
        isVoided
          ? `Voided signer card for ${signature.signer_name || 'signer'}`
          : undefined
      }
    >
      <div className="signer-card__header">
        <div>
          <div className="signer-card__name">
            {signature.signer_name || 'Unnamed signer'}
          </div>
          <div className="signer-card__role">
            {(signature.signer_role || 'signer').replace(/_/g, ' ')}
          </div>
        </div>
        {badgeKind && (
          <span
            className={
              `signer-card-mode-badge signer-card-mode-badge--${badgeKind}` +
              (badgeInactive ? ' signer-card-mode-badge--inactive' : '')
            }
          >
            {BADGE_COPY[badgeKind]}
          </span>
        )}
      </div>

      <div className="signer-card__body">{renderBody()}</div>

      <div className="signer-card__actions">{renderActions()}</div>
    </div>
  );
};

export default SignerStateCard;
